import {
  clearAuthTokens,
  getTokensFromStorage,
  isAccessTokenExpired,
  isRefreshTokenRejected,
  refreshAccessToken,
} from "./TokenService";
import { requestAnonymousChallenge } from "./AnonymousChallengeService";

export interface ApiResponse<T> {
  data: T;
  status: number;
}

export type RequestAuthMode = boolean | "optional";

interface PublicChallengePayload {
  code?: string;
  message?: string;
  error?: string;
  retryAfterSeconds?: number;
  captcha?: {
    provider?: string;
    siteKey?: string;
    action?: string;
  };
}

export default class FetchClient {
  private static baseUrl = import.meta.env.VITE_API_URL + "/v1";
  private static baseUrlNoAuth = import.meta.env.VITE_API_URL;

  private static sessionExpiredError() {
    const error = new Error("Your session has expired. Please sign in again.");
    (error as Error & { status: number }).status = 401;
    return error;
  }

  private static async request<T>(
    endpoint: string,
    options: RequestInit = {},
    authMode: RequestAuthMode = false,
    allowRefreshRetry: boolean = true,
    allowCaptchaRetry: boolean = true,
    captchaToken: string = ""
  ): Promise<T> {
    const noAuth = authMode === true;
    const optionalAuth = authMode === "optional";
    const tokens = noAuth ? null : getTokensFromStorage();
    let accessToken = tokens?.accessToken || "";
    let refreshedBeforeRequest = false;

    // A private request must never fall through to /v1 without credentials.
    // Besides avoiding a misleading server error, clearing here notifies the
    // app shell to discard any cached user left behind by the expired session.
    if (!noAuth && !optionalAuth && !tokens) {
      clearAuthTokens();
      throw this.sessionExpiredError();
    }

    if (!noAuth && tokens && isAccessTokenExpired(tokens.accessToken)) {
      try {
        accessToken = await refreshAccessToken(tokens.refreshToken);
        refreshedBeforeRequest = true;
      } catch (refreshError) {
        if (optionalAuth) {
          if (isRefreshTokenRejected(refreshError)) clearAuthTokens();
          accessToken = "";
        } else {
          if (isRefreshTokenRejected(refreshError)) {
            clearAuthTokens();
            throw this.sessionExpiredError();
          }
          // A network or server failure does not invalidate a refresh token.
          // Preserve the session so React Query (or the caller) can retry.
          throw refreshError;
        }
      }
    }

    const headers = {
      ...options.headers,
      ...(!noAuth && accessToken ? { Authorization: accessToken } : {}),
      ...(captchaToken ? { "X-DealHawk-Captcha-Token": captchaToken } : {}),
      "X-Source": "web",
      //   "Content-Type": "application/json",
    };

    const finalOptions = {
      ...options,
      headers,
    };

    const response = await fetch(
      noAuth || optionalAuth ? this.baseUrlNoAuth + endpoint : this.baseUrl + endpoint,
      finalOptions
    );

    if (
      response.status === 401 &&
      !noAuth &&
      !optionalAuth &&
      tokens?.refreshToken &&
      allowRefreshRetry &&
      !refreshedBeforeRequest
    ) {
      const currentTokens = getTokensFromStorage();
      try {
        if (!currentTokens?.refreshToken) {
          throw this.sessionExpiredError();
        }
        await refreshAccessToken(currentTokens.refreshToken);
        return this.request<T>(endpoint, options, authMode, false, allowCaptchaRetry, captchaToken);
      } catch (refreshError) {
        if (isRefreshTokenRejected(refreshError)) {
          clearAuthTokens();
          throw this.sessionExpiredError();
        }
        throw refreshError;
      }
    }

    if (response.status === 408 || response.status === 425) {
      const json = await response.json();
      const payload = json?.data ?? json;
      const errorObject = new Error(
        JSON.stringify({
          data: payload,
          message: payload?.error ?? json.message ?? json.error,
        })
      );
      (errorObject as Error & { status: number }).status = response.status;
      throw errorObject;
    }

    if (!response.ok) {
      const errorText = await response.text();
      // Try to parse as JSON first to extract message
      let errorMessage = errorText || "An error occurred while fetching data";
      let errorPayload: PublicChallengePayload | undefined;
      try {
        const errorJson = JSON.parse(errorText) as PublicChallengePayload;
        errorPayload = errorJson;
        if (errorJson.message) {
          errorMessage = errorJson.message;
        } else if (typeof errorJson.error === "string") {
          errorMessage = errorJson.error;
        }
      } catch {
        // If not JSON, use the text directly
        errorMessage = errorText || "An error occurred while fetching data";
      }

      const captcha = errorPayload?.captcha;
      if (
        response.status === 429 &&
        allowCaptchaRetry &&
        errorPayload?.code === "captcha_required" &&
        captcha?.provider === "turnstile" &&
        captcha.siteKey
      ) {
        const token = await requestAnonymousChallenge({
          provider: "turnstile",
          siteKey: captcha.siteKey,
          action: captcha.action || "public_catalog",
        });
        return this.request<T>(endpoint, options, authMode, allowRefreshRetry, false, token);
      }

      const errorObject = new Error(errorMessage);
      // Add status to error for better error handling
      const enrichedError = errorObject as Error & {
        status: number;
        code?: string;
        retryAfterSeconds?: number;
      };
      enrichedError.status = response.status;
      enrichedError.code = errorPayload?.code;
      enrichedError.retryAfterSeconds = errorPayload?.retryAfterSeconds;
      throw errorObject;
    }

    const text = await response.text();
    try {
      const json = JSON.parse(text);
      return json;
    } catch {
      return text as unknown as Promise<T>;
    }
  }

  static get<T>(
    endpoint: string,
    options: RequestInit = {},
    authMode: RequestAuthMode = false
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" }, authMode);
  }

  static patch<T>(
    endpoint: string,
    body: unknown,
    options: RequestInit = {},
    authMode?: RequestAuthMode
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: "PATCH",
        body: JSON.stringify(body),
      },
      authMode
    );
  }

  static post<T>(
    endpoint: string,
    body: unknown,
    options: RequestInit = {},
    authMode?: RequestAuthMode
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: "POST",
        body: JSON.stringify(body),
      },
      authMode
    );
  }

  // Multipart upload; browser sets the Content-Type boundary automatically.
  static postForm<T>(
    endpoint: string,
    form: FormData,
    options: RequestInit = {},
    authMode?: RequestAuthMode
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      { ...options, method: "POST", body: form },
      authMode
    );
  }

  static put<T>(
    endpoint: string,
    body: unknown,
    options: RequestInit = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  static delete<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }
}
